<template>
  <div id="landing-page" class="font-sans antialiased text-gray-900 bg-white">
    <!-- Header/Nav Placeholder (Optional, can add later) -->
    <!-- <header class="p-4 bg-blue-600 text-white">
      <nav class="container mx-auto flex justify-between items-center">
        <div class="text-2xl font-bold">Peekaboo</div>
        <div>Links</div>
      </nav>
    </header> -->

    <!-- Section 1: Hero -->
    <section class="bg-gradient-to-r from-blue-50 to-indigo-100 py-20 px-4">
      <div class="container mx-auto text-center max-w-3xl">
        <h1 class="text-4xl md:text-5xl font-bold mb-4 leading-tight">
          Find exactly when things happen in your videos—instantly.
        </h1>
        <p class="text-lg md:text-xl text-gray-700 mb-8">
          No more endless scrolling. Peebakoo instantly shows you the exact moment an object moves or disappears.
        </p>
        <!-- Updated Button to use Router Link -->
        <router-link 
          to="/tool"
          class="bg-blue-600 hover:bg-blue-700 text-white font-bold py-3 px-8 rounded-full text-lg transition duration-300 inline-block"
        >
          Try Peebakoo Now
        </router-link>
        <div class="mt-12 px-4">
          <!-- Ensure hero-image.png is in public/ directory -->
          <img src="/hero-image.png" alt="Peekaboo concept illustration" class="max-w-full h-auto mx-auto rounded-lg shadow-md">
        </div>
      </div>
    </section>

    <!-- Section 2: Problem & Solution -->
    <section class="py-16 px-4 bg-white">
      <div class="container mx-auto text-center">
        <h2 class="text-3xl font-bold mb-12">Why Peebakoo?</h2>
        <div class="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
          <div class="bg-gray-50 p-6 rounded-lg shadow-sm">
            <h3 class="text-xl font-semibold mb-2">🔍 Tired of scrubbing through hours of video?</h3>
            <p class="text-gray-600">Manually searching for a specific event in a long video is tedious and time-consuming.</p>
          </div>
          <div class="bg-blue-50 p-6 rounded-lg shadow-sm">
            <h3 class="text-xl font-semibold mb-2">🚀 Instantly find events.</h3>
            <p class="text-gray-600">Upload, mark an area, and Peebakoo finds exactly when an object moves or disappears, saving you hours.</p>
          </div>
          <div class="bg-gray-50 p-6 rounded-lg shadow-sm">
            <h3 class="text-xl font-semibold mb-2">🔒 Hard to pinpoint critical moments?</h3>
            <p class="text-gray-600">Finding security incidents or important events in videos is error-prone.</p>
          </div>
          <div class="bg-blue-50 p-6 rounded-lg shadow-sm">
            <h3 class="text-xl font-semibold mb-2">🎯 Accurate & Reliable AI.</h3>
            <p class="text-gray-600">Our intelligent AI precisely detects the moment you're looking for—every time.</p>
          </div>
        </div>
      </div>
    </section>

    <!-- Section 3: How It Works -->
    <section class="py-16 px-4 bg-gray-50">
      <div class="container mx-auto text-center">
        <h2 class="text-3xl font-bold mb-12">Just three easy steps.</h2>
        <div class="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
          <div class="text-center">
            <div class="bg-blue-500 text-white rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4 text-2xl font-bold">1</div>
            <h3 class="text-xl font-semibold mb-2">📤 Upload Your Video</h3>
            <p class="text-gray-600">Simply upload your video file through your browser—fast, secure, and easy.</p>
          </div>
          <div class="text-center">
            <div class="bg-blue-500 text-white rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4 text-2xl font-bold">2</div>
            <h3 class="text-xl font-semibold mb-2">📌 Select Your Area of Interest</h3>
            <p class="text-gray-600">Highlight the region or object you want Peebakoo to track.</p>
          </div>
          <div class="text-center">
            <div class="bg-blue-500 text-white rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4 text-2xl font-bold">3</div>
            <h3 class="text-xl font-semibold mb-2">🎉 Instantly Find the Moment</h3>
            <p class="text-gray-600">Let Peebakoo's AI instantly show the exact moment your object moves or disappears.</p>
          </div>
        </div>
         <!-- Updated Button to use Router Link -->
        <router-link 
          to="/tool"
          class="mt-12 bg-blue-600 hover:bg-blue-700 text-white font-bold py-3 px-8 rounded-full text-lg transition duration-300 inline-block"
          >
          Get Early Access
        </router-link>
      </div>
    </section>

    <!-- Section 4: Who Can Benefit -->
    <section class="py-16 px-4 bg-white">
      <div class="container mx-auto text-center">
        <h2 class="text-3xl font-bold mb-12">Built for everyone, useful everywhere.</h2>
        <div class="grid sm:grid-cols-2 lg:grid-cols-4 gap-8 max-w-6xl mx-auto">
          <div class="bg-gray-50 p-6 rounded-lg shadow-sm">
            <div class="text-3xl mb-3">🏠</div>
            <h3 class="text-lg font-semibold mb-1">Homeowners</h3>
            <p class="text-gray-600 text-sm">Quickly find when deliveries arrive or items disappear.</p>
          </div>
          <div class="bg-gray-50 p-6 rounded-lg shadow-sm">
            <div class="text-3xl mb-3">🛍️</div>
            <h3 class="text-lg font-semibold mb-1">Small Businesses</h3>
            <p class="text-gray-600 text-sm">Easily review security footage and pinpoint incidents.</p>
          </div>
          <div class="bg-gray-50 p-6 rounded-lg shadow-sm">
            <div class="text-3xl mb-3">🎥</div>
            <h3 class="text-lg font-semibold mb-1">Content Creators</h3>
            <p class="text-gray-600 text-sm">Skip scrubbing footage to find key moments.</p>
          </div>
          <div class="bg-gray-50 p-6 rounded-lg shadow-sm">
            <div class="text-3xl mb-3">🔎</div>
            <h3 class="text-lg font-semibold mb-1">Security Pros</h3>
            <p class="text-gray-600 text-sm">Rapidly analyze critical events from cameras.</p>
          </div>
        </div>
      </div>
    </section>

    <!-- Section 5: Testimonials -->
    <section class="py-16 px-4 bg-blue-50">
      <div class="container mx-auto text-center max-w-3xl">
        <h2 class="text-3xl font-bold mb-12">Trusted by people who value their time.</h2>
        <div class="bg-white p-8 rounded-lg shadow-md relative">
          <svg class="absolute top-4 left-4 w-8 h-8 text-blue-200" fill="currentColor" viewBox="0 0 32 32"><path d="M9.333 8h-4.667v13.333h4.667v-13.333zm18.667 0h-4.667v13.333h4.667v-13.333z"/></svg>
          <blockquote class="text-xl italic text-gray-700 mb-4">
            "Peebakoo saved me hours! Now I find security events in minutes, not hours."
          </blockquote>
          <cite class="block font-semibold text-gray-600">— [Customer Name], Small Business Owner</cite>
          <svg class="absolute bottom-4 right-4 w-8 h-8 text-blue-200 transform rotate-180" fill="currentColor" viewBox="0 0 32 32"><path d="M9.333 8h-4.667v13.333h4.667v-13.333zm18.667 0h-4.667v13.333h4.667v-13.333z"/></svg>
        </div>
        <!-- Add more testimonial placeholders as needed -->
      </div>
    </section>

    <!-- Section 6: Signup/Waitlist -->
    <section class="py-16 px-4 bg-gradient-to-r from-blue-600 to-indigo-700 text-white">
      <div class="container mx-auto text-center max-w-2xl">
        <h2 class="text-3xl font-bold mb-4">Get early access!</h2>
        <p class="text-lg mb-8">Sign up now and be the first to know when we launch.</p>
        <form @submit.prevent="handleWaitlistSubmit" class="flex flex-col sm:flex-row gap-4 justify-center">
          <input
            type="email"
            placeholder="Enter your email"
            v-model="waitlistEmail"
            :disabled="isLoading"
            class="flex-grow p-3 rounded text-gray-800 focus:outline-none focus:ring-2 focus:ring-blue-300 disabled:opacity-50"
            required>
          <button
            type="submit"
            :disabled="isLoading"
            class="bg-white text-blue-600 hover:bg-gray-100 font-bold py-3 px-8 rounded transition duration-300 disabled:opacity-50 disabled:cursor-not-allowed">
            <span v-if="isLoading">Submitting...</span>
            <span v-else>Join the Waitlist</span>
          </button>
        </form>
        <p v-if="submitMessage" :class="['mt-4 text-center', isError ? 'text-red-300' : 'text-green-300']">
          {{ submitMessage }}
        </p>
      </div>
    </section>

    <!-- Section 7: FAQ -->
    <section class="py-16 px-4 bg-white">
      <div class="container mx-auto max-w-3xl">
        <h2 class="text-3xl font-bold text-center mb-12">Questions? We've got answers.</h2>
        <div class="space-y-6">
          <div>
            <h3 class="font-semibold text-lg mb-1">Q: What types of videos can I upload?</h3>
            <p class="text-gray-600">A: Peebakoo supports all common formats (MP4, MOV, AVI, and more).</p>
          </div>
          <div>
            <h3 class="font-semibold text-lg mb-1">Q: How accurate is Peebakoo?</h3>
            <p class="text-gray-600">A: Our AI is continuously trained for accuracy, typically pinpointing events within a second.</p>
          </div>
          <div>
            <h3 class="font-semibold text-lg mb-1">Q: Is my video secure?</h3>
            <p class="text-gray-600">A: Absolutely. Videos are encrypted and processed securely. We delete them automatically shortly after analysis.</p>
          </div>
          <!-- Add more FAQs as needed -->
        </div>
      </div>
    </section>

    <!-- Section 8: Footer -->
    <footer class="bg-gray-800 text-gray-300 py-8 px-4">
      <div class="container mx-auto text-center">
        <div class="mb-4 space-x-6">
          <a href="#" class="hover:text-white">About</a>
          <a href="#" class="hover:text-white">Contact</a>
          <a href="#" class="hover:text-white">Privacy Policy</a>
          <a href="#" class="hover:text-white">Terms of Service</a>
        </div>
        <div class="mb-4 text-2xl space-x-4">
          <!-- Placeholder Social Icons -->
          <a href="#" class="hover:text-white">L</a>
          <a href="#" class="hover:text-white">X</a>
        </div>
        <p class="text-sm">&copy; 2025 Peebakoo. All Rights Reserved.</p>
      </div>
    </footer>

  </div>
</template>

<script>
export default {
  name: 'LandingPage',
  data() {
    return {
      waitlistEmail: '',
      isLoading: false,
      submitMessage: '',
      isError: false,
    };
  },
  methods: {
    async handleWaitlistSubmit() {
      if (!this.waitlistEmail || !this.waitlistEmail.includes('@')) {
        this.submitMessage = 'Please enter a valid email address.';
        this.isError = true;
        return;
      }

      this.isLoading = true;
      this.submitMessage = '';
      this.isError = false;

      try {
        // Note: Ensure API endpoint is correct (absolute or proxied)
        const response = await fetch('http://localhost:5000/api/waitlist', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ email: this.waitlistEmail }),
        });

        const result = await response.json();

        if (!response.ok) {
          // Handle backend errors (like 400 Bad Request)
          throw new Error(result.error || `HTTP error! status: ${response.status}`);
        }

        // Success
        this.submitMessage = result.message || 'Success! You have joined the waitlist.';
        this.isError = false;
        this.waitlistEmail = ''; // Clear the input on success

      } catch (error) {
        console.error('Waitlist submission error:', error);
        this.submitMessage = error.message || 'An error occurred. Please try again.';
        this.isError = true;
      } finally {
        this.isLoading = false;
      }
    },
  },
}
</script>

<style>
/* Optional: Add any global styles or scoped styles if needed */
</style> 